/**
 * Procedural silhouettes. §7.7's readability bar is identification at 48 px, so each
 * shape is built from two or three bold masses rather than from fine detail, and no
 * two archetypes share a mass layout.
 *
 * Spine §1 bans the Geometry Wars vocabulary outright: nothing here is a diamond, a
 * pinwheel, a square, a cube or a segmented snake. Every form is a deep-sea organism —
 * teardrops with fins, bells with tentacles, eel ribbons, crab plates, anemone fronds,
 * siphonophore chains.
 *
 * Geometry is 2D triangle soup in the XY plane, normalised so the furthest vertex sits
 * at radius 1. Instance scale is therefore the entity's world radius. `position.xy`
 * doubles as the local coordinate the fragment shaders read, so no extra attribute
 * exists. All construction happens at boot; nothing here runs per frame.
 */

import * as THREE from 'three';

const TAU = Math.PI * 2;

/** Triangle-soup accumulator. Allocates freely: every call site is boot-time. */
export class ShapeBuilder {
  private readonly data: number[] = [];

  tri(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): this {
    this.data.push(ax, ay, 0, bx, by, 0, cx, cy, 0);
    return this;
  }

  quad(ax: number, ay: number, bx: number, by: number, cx: number, cy: number, dx: number, dy: number): this {
    this.tri(ax, ay, bx, by, cx, cy);
    this.tri(ax, ay, cx, cy, dx, dy);
    return this;
  }

  /** Fans a closed point ring from its centroid. Valid for star-shaped outlines. */
  fan(pts: readonly number[], cx?: number, cy?: number): this {
    const n = pts.length / 2;
    if (n < 3) return this;
    let ox = cx ?? 0;
    let oy = cy ?? 0;
    if (cx === undefined) {
      ox = 0;
      oy = 0;
      for (let i = 0; i < n; i++) {
        ox += pts[i * 2];
        oy += pts[i * 2 + 1];
      }
      ox /= n;
      oy /= n;
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      this.tri(ox, oy, pts[i * 2], pts[i * 2 + 1], pts[j * 2], pts[j * 2 + 1]);
    }
    return this;
  }

  ellipse(cx: number, cy: number, rx: number, ry: number, seg = 24, rot = 0): this {
    const c = Math.cos(rot);
    const s = Math.sin(rot);
    for (let i = 0; i < seg; i++) {
      const a0 = (i / seg) * TAU;
      const a1 = ((i + 1) / seg) * TAU;
      const x0 = Math.cos(a0) * rx;
      const y0 = Math.sin(a0) * ry;
      const x1 = Math.cos(a1) * rx;
      const y1 = Math.sin(a1) * ry;
      this.tri(cx, cy, cx + x0 * c - y0 * s, cy + x0 * s + y0 * c, cx + x1 * c - y1 * s, cy + x1 * s + y1 * c);
    }
    return this;
  }

  /** Annulus sector: the workhorse for bells, jaws, plates and rims. */
  arcBand(cx: number, cy: number, rIn: number, rOut: number, a0: number, a1: number, seg = 20): this {
    for (let i = 0; i < seg; i++) {
      const t0 = a0 + ((a1 - a0) * i) / seg;
      const t1 = a0 + ((a1 - a0) * (i + 1)) / seg;
      const c0 = Math.cos(t0);
      const s0 = Math.sin(t0);
      const c1 = Math.cos(t1);
      const s1 = Math.sin(t1);
      this.quad(
        cx + c0 * rIn, cy + s0 * rIn,
        cx + c0 * rOut, cy + s0 * rOut,
        cx + c1 * rOut, cy + s1 * rOut,
        cx + c1 * rIn, cy + s1 * rIn,
      );
    }
    return this;
  }

  /** Straight tapered strip — fins, arms, tentacles, antennae. */
  taper(x0: number, y0: number, x1: number, y1: number, w0: number, w1: number): this {
    const dx = x1 - x0;
    const dy = y1 - y0;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    return this.quad(
      x0 + nx * w0, y0 + ny * w0,
      x1 + nx * w1, y1 + ny * w1,
      x1 - nx * w1, y1 - ny * w1,
      x0 - nx * w0, y0 - ny * w0,
    );
  }

  /** Curved tapered strip through a quadratic arc. Tentacles and stalks bend. */
  curveTaper(
    x0: number, y0: number,
    cxp: number, cyp: number,
    x1: number, y1: number,
    w0: number, w1: number,
    seg = 8,
  ): this {
    let px = x0;
    let py = y0;
    for (let i = 1; i <= seg; i++) {
      const t = i / seg;
      const it = 1 - t;
      const qx = it * it * x0 + 2 * it * t * cxp + t * t * x1;
      const qy = it * it * y0 + 2 * it * t * cyp + t * t * y1;
      this.taper(px, py, qx, qy, w0 + (w1 - w0) * ((i - 1) / seg), w0 + (w1 - w0) * t);
      px = qx;
      py = qy;
    }
    return this;
  }

  /**
   * Closed body from a half-width profile sampled along X. `half(u)` takes u in 0..1
   * running tail (xMin) to nose (xMax) and returns the half-height there.
   */
  profile(xMin: number, xMax: number, seg: number, half: (u: number) => number): this {
    for (let i = 0; i < seg; i++) {
      const u0 = i / seg;
      const u1 = (i + 1) / seg;
      const x0 = xMin + (xMax - xMin) * u0;
      const x1 = xMin + (xMax - xMin) * u1;
      const h0 = half(u0);
      const h1 = half(u1);
      this.quad(x0, -h0, x0, h0, x1, h1, x1, -h1);
    }
    return this;
  }

  /** Scales every vertex so the furthest sits at radius 1. */
  normalize(): this {
    let max = 0;
    for (let i = 0; i < this.data.length; i += 3) {
      const r = Math.hypot(this.data[i], this.data[i + 1]);
      if (r > max) max = r;
    }
    if (max > 0) {
      const k = 1 / max;
      for (let i = 0; i < this.data.length; i += 3) {
        this.data[i] *= k;
        this.data[i + 1] *= k;
      }
    }
    return this;
  }

  get triangleCount(): number {
    return this.data.length / 9;
  }

  build(name: string): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(this.data), 3));
    g.name = name;
    return g;
  }
}

function b(): ShapeBuilder {
  return new ShapeBuilder();
}

// --- 14 enemy archetypes (enemies.json silhouette strings) -------------------------

/** Plump teardrop body, single tail fin, chin-barbel glow dot. */
function driftfin(): ShapeBuilder {
  const s = b();
  s.profile(-0.85, 1.0, 18, (u) => 0.62 * Math.pow(u, 0.42) * (1 - 0.5 * Math.pow(u, 5)));
  s.tri(-0.72, 0.18, -1.35, 0.62, -1.3, -0.05);
  s.tri(-0.72, -0.18, -1.3, 0.05, -1.35, -0.62);
  s.ellipse(0.72, -0.42, 0.15, 0.15, 10);
  s.taper(0.62, -0.28, 0.72, -0.42, 0.05, 0.05);
  return s;
}

/** Comma tadpole, oversized eye dot. */
function glimmerfry(): ShapeBuilder {
  const s = b();
  s.ellipse(0.34, 0, 0.62, 0.56, 20);
  s.curveTaper(0.0, 0.12, -0.6, 0.55, -1.25, 0.22, 0.34, 0.03, 8);
  s.ellipse(0.58, 0.14, 0.19, 0.19, 12);
  return s;
}

/** Needle body, forked tail, one lateral stripe. */
function dartEel(): ShapeBuilder {
  const s = b();
  s.profile(-0.9, 1.0, 20, (u) => 0.2 * Math.pow(Math.sin(Math.PI * Math.pow(u, 0.8)), 0.6) + 0.02);
  s.tri(-0.85, 0.05, -1.5, 0.42, -1.15, 0.0);
  s.tri(-0.85, -0.05, -1.15, 0.0, -1.5, -0.42);
  s.taper(-0.4, 0.0, 0.7, 0.0, 0.05, 0.03);
  return s;
}

/** Conical shell, protruding siphon tube, stubby hopping base. */
function ventspitter(): ShapeBuilder {
  const s = b();
  s.fan([-0.62, -0.62, -0.62, 0.72, 0.15, 0.5, 0.62, 0.0, 0.15, -0.5]);
  s.taper(0.4, 0.06, 1.05, 0.06, 0.13, 0.08);
  s.quad(-0.55, -0.62, 0.2, -0.62, 0.05, -0.92, -0.45, -0.92);
  s.arcBand(-0.62, 0.05, 0.2, 0.36, Math.PI * 0.55, Math.PI * 1.45, 10);
  return s;
}

/** Pulsing bell, 4 stubby arms, 3 visible internal nuclei. */
function bloomJelly(): ShapeBuilder {
  const s = b();
  s.arcBand(0, 0, 0.0, 0.92, 0, Math.PI, 22);
  s.quad(-0.92, 0, 0.92, 0, 0.78, -0.16, -0.78, -0.16);
  for (let i = 0; i < 4; i++) {
    const x = -0.6 + i * 0.4;
    s.taper(x, -0.12, x * 0.85, -0.78, 0.11, 0.05);
  }
  s.ellipse(-0.34, 0.34, 0.13, 0.13, 10);
  s.ellipse(0.0, 0.5, 0.13, 0.13, 10);
  s.ellipse(0.34, 0.34, 0.13, 0.13, 10);
  return s;
}

/** Wide hexagonal carapace, two claw nubs, squat leg fringe. */
function basaltCrab(): ShapeBuilder {
  const s = b();
  s.fan([-0.5, -0.6, -0.85, 0.0, -0.5, 0.6, 0.5, 0.6, 0.9, 0.0, 0.5, -0.6]);
  s.ellipse(0.82, 0.46, 0.24, 0.18, 10);
  s.ellipse(0.82, -0.46, 0.24, 0.18, 10);
  for (let i = 0; i < 3; i++) {
    const x = -0.45 + i * 0.42;
    s.tri(x, 0.55, x + 0.1, 0.95, x + 0.2, 0.55);
    s.tri(x, -0.55, x + 0.2, -0.55, x + 0.1, -0.95);
  }
  return s;
}

/** Torpedo body, long rostrum spike, swept-back fins. */
function spearnose(): ShapeBuilder {
  const s = b();
  s.profile(-0.75, 0.6, 16, (u) => 0.46 * Math.sin(Math.PI * Math.pow(u, 0.75)) + 0.04);
  s.tri(0.5, 0.16, 1.5, 0.0, 0.5, -0.16);
  s.tri(-0.2, 0.28, -0.85, 0.85, -0.62, 0.16);
  s.tri(-0.2, -0.28, -0.62, -0.16, -0.85, -0.85);
  return s;
}

/** Rooted radial polyp crown, 8 feathery arms. */
function nettleAnemone(): ShapeBuilder {
  const s = b();
  s.ellipse(0, 0, 0.36, 0.36, 16);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU + 0.19;
    const cx = Math.cos(a);
    const cy = Math.sin(a);
    s.curveTaper(cx * 0.28, cy * 0.28, cx * 0.7 - cy * 0.24, cy * 0.7 + cx * 0.24, cx, cy, 0.13, 0.03, 5);
    s.ellipse(cx * 0.98, cy * 0.98, 0.07, 0.07, 6);
  }
  return s;
}

/** Wide umbrella bell, inner ring of bead-lights, long oral arms. */
function aegisMedusa(): ShapeBuilder {
  const s = b();
  s.arcBand(0, -0.1, 0.0, 1.0, 0.12, Math.PI - 0.12, 24);
  for (let i = 0; i < 7; i++) {
    const a = 0.3 + (i / 6) * (Math.PI - 0.6);
    s.ellipse(Math.cos(a) * 0.62, -0.1 + Math.sin(a) * 0.36, 0.08, 0.08, 6);
  }
  s.curveTaper(-0.3, -0.12, -0.44, -0.6, -0.2, -1.0, 0.1, 0.03, 6);
  s.curveTaper(0.3, -0.12, 0.44, -0.6, 0.2, -1.0, 0.1, 0.03, 6);
  s.quad(-0.98, -0.1, 0.98, -0.1, 0.84, -0.26, -0.84, -0.26);
  return s;
}

/** Segmented slug, ringed sucker mouth, pulsing dorsal nodules. */
function siphonLeech(): ShapeBuilder {
  const s = b();
  for (let i = 0; i < 5; i++) {
    const x = 0.62 - i * 0.36;
    s.ellipse(x, 0, 0.28 - i * 0.03, 0.42 - i * 0.055, 14);
  }
  s.arcBand(0.82, 0, 0.2, 0.34, 0, TAU, 16);
  s.ellipse(0.18, 0.4, 0.11, 0.11, 8);
  s.ellipse(-0.18, 0.36, 0.1, 0.1, 8);
  s.ellipse(-0.52, 0.3, 0.09, 0.09, 8);
  return s;
}

/** Elongated mantle, two lateral fins, arm crown, dark ink-sac core. */
function gloomsquid(): ShapeBuilder {
  const s = b();
  s.profile(-1.0, 0.42, 16, (u) => 0.42 * Math.pow(u, 0.55));
  s.tri(-0.5, 0.32, -0.98, 0.82, -0.9, 0.18);
  s.tri(-0.5, -0.32, -0.9, -0.18, -0.98, -0.82);
  for (let i = 0; i < 6; i++) {
    const y = -0.34 + (i / 5) * 0.68;
    s.curveTaper(0.4, y * 0.8, 0.72, y * 1.25, 0.95, y * 1.7, 0.07, 0.02, 4);
  }
  s.ellipse(-0.34, 0, 0.19, 0.19, 10);
  return s;
}

/** Huge crescent jaw, dorsal stalk with a small pulsing lure orb, tiny fins. */
function luremaw(): ShapeBuilder {
  const s = b();
  s.arcBand(-0.1, 0, 0.55, 1.0, -Math.PI * 0.62, Math.PI * 0.62, 22);
  s.tri(0.28, 0.5, 0.72, 0.16, 0.28, 0.08);
  s.tri(0.28, -0.5, 0.28, -0.08, 0.72, -0.16);
  s.curveTaper(-0.2, 0.6, 0.3, 1.05, 0.72, 0.86, 0.08, 0.04, 6);
  s.ellipse(0.78, 0.88, 0.17, 0.17, 12);
  s.tri(-0.72, 0.22, -1.05, 0.5, -0.86, 0.05);
  s.tri(-0.72, -0.22, -0.86, -0.05, -1.05, -0.5);
  return s;
}

/** Arc of joined bells on a shared stolon, pulsing in sequence. */
function colonialCurtain(): ShapeBuilder {
  const s = b();
  const count = 7;
  for (let i = 0; i < count; i++) {
    const t = (i / (count - 1)) * 2 - 1;
    const a = t * 0.95;
    const cx = Math.sin(a) * 1.05;
    const cy = -0.35 + Math.cos(a) * 0.32;
    s.arcBand(cx, cy, 0, 0.3, a, Math.PI + a, 10);
    s.taper(cx, cy, cx - Math.sin(a) * 0.05, cy - 0.34, 0.14, 0.05);
  }
  for (let i = 0; i < count - 1; i++) {
    const t0 = (i / (count - 1)) * 2 - 1;
    const t1 = ((i + 1) / (count - 1)) * 2 - 1;
    const a0 = t0 * 0.95;
    const a1 = t1 * 0.95;
    s.taper(Math.sin(a0) * 1.05, -0.35 + Math.cos(a0) * 0.32, Math.sin(a1) * 1.05, -0.35 + Math.cos(a1) * 0.32, 0.06, 0.06);
  }
  return s;
}

/** Massive bell with 3 glowing gestation pods inside, stout arms. */
function broodBell(): ShapeBuilder {
  const s = b();
  s.arcBand(0, -0.15, 0, 1.0, -0.22, Math.PI + 0.22, 26);
  s.quad(-0.98, -0.15, 0.98, -0.15, 0.8, -0.34, -0.8, -0.34);
  s.ellipse(-0.38, 0.18, 0.2, 0.24, 12);
  s.ellipse(0.0, 0.34, 0.2, 0.24, 12);
  s.ellipse(0.38, 0.18, 0.2, 0.24, 12);
  s.taper(-0.5, -0.3, -0.58, -0.92, 0.15, 0.07);
  s.taper(0.0, -0.3, 0.0, -1.0, 0.16, 0.07);
  s.taper(0.5, -0.3, 0.58, -0.92, 0.15, 0.07);
  return s;
}

/** Ordered exactly as the §14.2 draw-call table's 14 archetype meshes. */
export const ENEMY_DEF_IDS: readonly string[] = [
  'enemy.driftfin',
  'enemy.glimmerfry',
  'enemy.dart-eel',
  'enemy.ventspitter',
  'enemy.bloom-jelly',
  'enemy.basalt-crab',
  'enemy.spearnose',
  'enemy.nettle-anemone',
  'enemy.aegis-medusa',
  'enemy.siphon-leech',
  'enemy.gloomsquid',
  'enemy.luremaw',
  'enemy.colonial-curtain',
  'enemy.brood-bell',
];

const ENEMY_BUILDERS: readonly (() => ShapeBuilder)[] = [
  driftfin,
  glimmerfry,
  dartEel,
  ventspitter,
  bloomJelly,
  basaltCrab,
  spearnose,
  nettleAnemone,
  aegisMedusa,
  siphonLeech,
  gloomsquid,
  luremaw,
  colonialCurtain,
  broodBell,
];

const ENEMY_SLOT = new Map<string, number>();
for (let i = 0; i < ENEMY_DEF_IDS.length; i++) ENEMY_SLOT.set(ENEMY_DEF_IDS[i], i);

/** Archetype mesh slot for a `def_id`. Unknown ids fall back to driftfin. */
export function enemySlot(defId: string): number {
  const i = ENEMY_SLOT.get(defId);
  return i === undefined ? 0 : i;
}

export function buildEnemyGeometry(slot: number): THREE.BufferGeometry {
  const f = ENEMY_BUILDERS[slot] ?? ENEMY_BUILDERS[0];
  return f().normalize().build(`enemy-${ENEMY_DEF_IDS[slot] ?? 'unknown'}`);
}

// --- player -----------------------------------------------------------------------

/**
 * Spine §12's warm-gold manta — swept wings, notched trailing edge, no tail fin — lives
 * in `ship.ts` now, because AMD-43 generates one per run seed from the parameters that
 * sentence names. `REFERENCE_HULL` there is this eight-point fan, unchanged, and it is
 * still what every generated hull is measured against for family membership.
 */

/** Soft aura disc drawn behind the player; the emissive quad of §14.2's 2 player meshes. */
export function buildAuraGeometry(): THREE.BufferGeometry {
  return b().ellipse(0, 0, 1, 1, 32).build('player-aura');
}

// --- bosses -----------------------------------------------------------------------

/** Apex anglerfish: oversized crescent jaw, dorsal lure stalk, heavy pectoral fins. */
function lanternTyrant(): ShapeBuilder {
  const s = b();
  s.profile(-0.9, 0.35, 16, (u) => 0.72 * Math.pow(u, 0.5));
  s.arcBand(0.3, 0, 0.42, 0.86, -Math.PI * 0.7, Math.PI * 0.7, 22);
  for (let i = 0; i < 7; i++) {
    const a = -Math.PI * 0.55 + (i / 6) * Math.PI * 1.1;
    s.tri(0.3 + Math.cos(a) * 0.44, Math.sin(a) * 0.44, 0.3 + Math.cos(a) * 0.16, Math.sin(a) * 0.2, 0.3 + Math.cos(a + 0.2) * 0.44, Math.sin(a + 0.2) * 0.44);
  }
  s.curveTaper(-0.25, 0.6, 0.35, 1.3, 0.95, 1.0, 0.1, 0.05, 8);
  s.ellipse(1.02, 1.02, 0.2, 0.2, 14);
  s.tri(-0.2, 0.45, -0.95, 1.0, -0.85, 0.28);
  s.tri(-0.2, -0.45, -0.85, -0.28, -0.95, -1.0);
  return s;
}

/** Giant isopod: segmented plate armour, antenna whips, rolling gait. */
function abyssalWarden(): ShapeBuilder {
  const s = b();
  for (let i = 0; i < 6; i++) {
    const x = 0.62 - i * 0.28;
    const h = 0.85 - Math.abs(i - 2) * 0.09;
    s.arcBand(x, 0, 0.0, h, Math.PI * 0.5, Math.PI * 1.5, 12);
    s.arcBand(x, 0, 0.0, h, -Math.PI * 0.5, Math.PI * 0.5, 12);
    s.taper(x, -h, x, h, 0.0, 0.0);
  }
  s.curveTaper(0.7, 0.3, 1.2, 0.7, 1.5, 0.35, 0.07, 0.02, 6);
  s.curveTaper(0.7, -0.3, 1.2, -0.7, 1.5, -0.35, 0.07, 0.02, 6);
  for (let i = 0; i < 4; i++) {
    const x = 0.4 - i * 0.32;
    s.taper(x, 0.75, x - 0.12, 1.12, 0.08, 0.03);
    s.taper(x, -0.75, x - 0.12, -1.12, 0.08, 0.03);
  }
  return s;
}

/** Giant crown jelly: domed bell with spine fringe, 4 slow ribbon arms. */
function crownMedusa(): ShapeBuilder {
  const s = b();
  s.arcBand(0, -0.2, 0, 1.0, -0.1, Math.PI + 0.1, 28);
  for (let i = 0; i < 13; i++) {
    const a = -0.05 + (i / 12) * (Math.PI + 0.1);
    s.tri(
      Math.cos(a) * 0.96, -0.2 + Math.sin(a) * 0.96,
      Math.cos(a + 0.09) * 0.96, -0.2 + Math.sin(a + 0.09) * 0.96,
      Math.cos(a + 0.045) * 1.22, -0.2 + Math.sin(a + 0.045) * 1.22,
    );
  }
  s.quad(-1.0, -0.2, 1.0, -0.2, 0.82, -0.4, -0.82, -0.4);
  for (let i = 0; i < 4; i++) {
    const x = -0.54 + i * 0.36;
    s.curveTaper(x, -0.36, x * 1.5, -0.8, x * 1.1, -1.25, 0.1, 0.03, 7);
  }
  return s;
}

/** Colossal whale-fall leviathan: ribcage hull, lantern esca crown, trailing fin veil. */
function hadalSovereign(): ShapeBuilder {
  const s = b();
  s.profile(-1.0, 0.75, 20, (u) => 0.6 * Math.pow(u, 0.4) * (1 - 0.25 * u));
  for (let i = 0; i < 6; i++) {
    const x = 0.5 - i * 0.24;
    const h = 0.62 + Math.sin((i / 5) * Math.PI) * 0.36;
    s.arcBand(x, 0, h - 0.09, h, -Math.PI * 0.5, Math.PI * 0.5, 10);
    s.arcBand(x, 0, h - 0.09, h, Math.PI * 0.5, Math.PI * 1.5, 10);
  }
  for (let i = 0; i < 5; i++) {
    const a = -0.5 + (i / 4) * 1.0;
    s.taper(0.7, Math.sin(a) * 0.34, 1.05 + Math.cos(a) * 0.15, Math.sin(a) * 0.7, 0.06, 0.03);
    s.ellipse(1.12 + Math.cos(a) * 0.15, Math.sin(a) * 0.78, 0.11, 0.11, 8);
  }
  s.curveTaper(-0.9, 0.2, -1.3, 0.7, -1.05, 1.05, 0.12, 0.03, 7);
  s.curveTaper(-0.9, -0.2, -1.3, -0.7, -1.05, -1.05, 0.12, 0.03, 7);
  return s;
}

export const BOSS_IDS: readonly string[] = [
  'boss.lantern-tyrant',
  'boss.abyssal-warden',
  'boss.crown-medusa',
  'boss.hadal-sovereign',
];

const BOSS_BUILDERS: readonly (() => ShapeBuilder)[] = [
  lanternTyrant,
  abyssalWarden,
  crownMedusa,
  hadalSovereign,
];

const BOSS_SLOT = new Map<string, number>();
for (let i = 0; i < BOSS_IDS.length; i++) BOSS_SLOT.set(BOSS_IDS[i], i);

export function bossSlot(defId: string): number {
  const i = BOSS_SLOT.get(defId);
  return i === undefined ? 0 : i;
}

export function buildBossGeometry(slot: number): THREE.BufferGeometry {
  const f = BOSS_BUILDERS[slot] ?? BOSS_BUILDERS[0];
  return f().normalize().build(`boss-${BOSS_IDS[slot] ?? 'unknown'}`);
}

// --- projectiles (§14.2's 12 player + 3 enemy visual classes) ----------------------

function projSpear(): ShapeBuilder {
  return b()
    .fan([1.0, 0.0, 0.1, 0.28, -0.9, 0.12, -0.9, -0.12, 0.1, -0.28]);
}

function projKrillPellet(): ShapeBuilder {
  const s = b();
  s.ellipse(0.1, 0, 0.7, 0.44, 14);
  s.taper(-0.4, 0.1, -0.95, 0.4, 0.07, 0.03);
  s.taper(-0.4, -0.1, -0.95, -0.4, 0.07, 0.03);
  return s;
}

/** Unit beam: x runs 0..1 along the beam, y is the half-width. Never normalised. */
function projBeam(): ShapeBuilder {
  return b().quad(0, -1, 1, -1, 1, 1, 0, 1);
}

function projHaloOrb(): ShapeBuilder {
  const s = b();
  s.ellipse(0.3, 0, 0.6, 0.6, 18);
  s.fan([-0.1, 0.42, -1.0, 0.0, -0.1, -0.42]);
  return s;
}

function projNovaRing(): ShapeBuilder {
  return b().arcBand(0, 0, 0.74, 1.0, 0, TAU, 34);
}

function projMine(): ShapeBuilder {
  const s = b();
  s.fan([0.72, 0.36, 0.0, 0.66, -0.72, 0.36, -0.72, -0.36, 0.0, -0.66, 0.72, -0.36]);
  s.taper(0.0, 0.62, 0.0, 1.0, 0.09, 0.05);
  s.ellipse(0, 1.02, 0.16, 0.16, 10);
  return s;
}

function projChainArc(): ShapeBuilder {
  const s = b();
  s.ellipse(-0.62, 0, 0.34, 0.34, 12);
  s.ellipse(0.0, 0.1, 0.4, 0.4, 14);
  s.ellipse(0.62, 0, 0.34, 0.34, 12);
  s.taper(-0.62, 0, 0.62, 0, 0.09, 0.09);
  return s;
}

function projScion(): ShapeBuilder {
  const s = b();
  s.profile(-0.7, 0.3, 10, (u) => 0.36 * Math.pow(u, 0.5));
  for (let i = 0; i < 4; i++) {
    const y = -0.24 + (i / 3) * 0.48;
    s.taper(0.28, y * 0.7, 0.8, y * 1.5, 0.07, 0.02);
  }
  return s;
}

function projZonePool(): ShapeBuilder {
  const s = b();
  s.ellipse(0, 0, 1.0, 1.0, 30);
  return s;
}

function projShroud(): ShapeBuilder {
  const s = b();
  s.arcBand(0, 0, 0.8, 1.0, 0, TAU, 6);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU;
    s.taper(0, 0, Math.cos(a) * 0.9, Math.sin(a) * 0.9, 0.05, 0.05);
  }
  return s;
}

function projMaw(): ShapeBuilder {
  return b().arcBand(-0.25, 0, 0.55, 1.0, -Math.PI * 0.6, Math.PI * 0.6, 20);
}

function projVentSpit(): ShapeBuilder {
  const s = b();
  s.ellipse(0, 0, 0.85, 0.7, 14);
  s.tri(-0.6, 0.32, -1.0, 0.0, -0.6, -0.32);
  return s;
}

function projInkGlob(): ShapeBuilder {
  const s = b();
  s.fan([0.9, 0.15, 0.35, 0.75, -0.3, 0.6, -0.85, 0.1, -0.4, -0.65, 0.3, -0.8]);
  return s;
}

function projBristleDart(): ShapeBuilder {
  const s = b();
  s.fan([1.0, 0.0, -0.5, 0.16, -0.85, 0.0, -0.5, -0.16]);
  s.tri(-0.2, 0.14, -0.7, 0.42, -0.55, 0.06);
  s.tri(-0.2, -0.14, -0.55, -0.06, -0.7, -0.42);
  return s;
}

const PROJECTILE_BUILDERS: readonly (() => ShapeBuilder)[] = [
  projSpear,
  projKrillPellet,
  projBeam,
  projHaloOrb,
  projNovaRing,
  projMine,
  projChainArc,
  projScion,
  projZonePool,
  projShroud,
  projMaw,
  projBeam,
  projVentSpit,
  projInkGlob,
  projBristleDart,
];

/** Beam classes keep their 0..1 parametrisation; everything else is unit-normalised. */
export function buildProjectileGeometry(vc: number, isBeam: boolean): THREE.BufferGeometry {
  const f = PROJECTILE_BUILDERS[vc] ?? PROJECTILE_BUILDERS[0];
  const s = f();
  if (!isBeam) s.normalize();
  return s.build(`projectile-vc${vc}`);
}

// --- pickups, hazards, particles ---------------------------------------------------

/**
 * Spine §12 XP mote: a rounded droplet with a trailing tail and a gradient core.
 * Explicitly NOT a diamond or a bare rhombus — that silhouette is banned by spine §1.
 */
export function buildMoteGeometry(): THREE.BufferGeometry {
  const s = b();
  s.ellipse(0.05, 0, 0.72, 0.72, 20);
  s.fan([-0.4, 0.5, -1.0, 0.0, -0.4, -0.5]);
  return s.normalize().build('xp-mote');
}

/** Health pickup: an original four-petal lumen cross (§11.12 rule 2 third code). */
export function buildHealthGeometry(): THREE.BufferGeometry {
  const s = b();
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU;
    const cx = Math.cos(a);
    const cy = Math.sin(a);
    s.taper(cx * 0.12, cy * 0.12, cx * 0.78, cy * 0.78, 0.26, 0.2);
    s.ellipse(cx * 0.78, cy * 0.78, 0.22, 0.22, 10);
  }
  s.ellipse(0, 0, 0.3, 0.3, 12);
  return s.normalize().build('health-pickup');
}

/** Power-up: a warm faceted lozenge, distinct from both mote and health silhouettes. */
export function buildPowerupGeometry(): THREE.BufferGeometry {
  const s = b();
  s.fan([0.0, 1.0, 0.62, 0.42, 0.5, -0.5, 0.0, -0.86, -0.5, -0.5, -0.62, 0.42]);
  s.ellipse(0, 0, 0.34, 0.34, 12);
  return s.normalize().build('powerup');
}

export function buildQuadGeometry(): THREE.BufferGeometry {
  return b().quad(-1, -1, 1, -1, 1, 1, -1, 1).build('quad');
}

/**
 * AMD-6 D14's off-screen threat chevron, in world space. Retired by AMD-8: the arrows
 * are now screen-space SDF quads drawn past the post chain (`radar.ts`), so no batch
 * builds this any more. Kept exported because the shape is the reference for the
 * arrowhead SDF and deleting it would lose that.
 */
export function buildChevronGeometry(): THREE.BufferGeometry {
  const s = b();
  s.quad(1.0, 0.0, 0.05, 0.78, -0.28, 0.5, 0.55, 0.0);
  s.quad(1.0, 0.0, -0.28, -0.5, 0.05, -0.78, 0.55, 0.0);
  return s.build('edge-chevron');
}


export function buildDiscGeometry(segments = 40): THREE.BufferGeometry {
  return b().ellipse(0, 0, 1, 1, segments).build('disc');
}

/** Annulus at 0.82..1.0, used for rings, rims, outlines and hazard edges. */
export function buildRingGeometry(segments = 44, inner = 0.82): THREE.BufferGeometry {
  return b().arcBand(0, 0, inner, 1, 0, TAU, segments).build('ring');
}

/** Beam quad in 0..1 × -1..1, shared by aim lines and both beam weapon classes. */
export function buildBeamGeometry(): THREE.BufferGeometry {
  return projBeam().build('beam');
}

/**
 * Rectangular frame band, for the striped edge of a rectangular hazard (§11.9). The
 * band is 0.4 u wide at unit scale, matching the spec's 0.40 u striped edge.
 */
export function buildFrameGeometry(thickness = 0.2): THREE.BufferGeometry {
  const s = b();
  const o = 1;
  const i = 1 - thickness;
  s.quad(-o, i, o, i, o, o, -o, o);
  s.quad(-o, -o, o, -o, o, -i, -o, -i);
  s.quad(-o, -i, -i, -i, -i, i, -o, i);
  s.quad(i, -i, o, -i, o, i, i, i);
  return s.build('frame');
}
